import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Header from "../components/Header";
import DealCard from "../components/DealCard";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import FAQSection from "../components/FAQSection";
import { Smartphone, Laptop, Tablet, Headphones, Watch, ArrowRight, Users, Clock, Shield, Sparkles, RefreshCw, Award, CreditCard, PackageCheck, Truck, CheckCircle } from "lucide-react";

const categories = [
  { id: "all", label: "Todos", icon: null },
  { id: "iphone", label: "iPhone", icon: Smartphone },
  { id: "macbook", label: "MacBook", icon: Laptop },
  { id: "ipad", label: "iPad", icon: Tablet },
  { id: "airpods", label: "AirPods", icon: Headphones },
  { id: "watch", label: "Watch", icon: Watch },
];

const conditions = [
  { id: "all", label: "Todos", icon: null, description: "Ver todas las ofertas" },
  { id: "new", label: "Nuevo", icon: Sparkles, description: "Productos nuevos de fábrica" },
  { id: "cpo", label: "CPO", icon: Award, description: "Certificado por Apple" },
  { id: "used", label: "Usado", icon: RefreshCw, description: "Segunda mano verificado" },
];

const howItWorks = [
  {
    step: 1,
    icon: Users,
    title: "Elige tu producto",
    description: "Explora las ofertas activas de productos Apple nuevos, usados o reacondicionados."
  },
  {
    step: 2,
    icon: CreditCard,
    title: "Únete y paga",
    description: "Apúntate a la oferta y realiza el pago. Tu dinero queda protegido."
  },
  {
    step: 3,
    icon: Clock,
    title: "Espera al grupo",
    description: "Si se alcanza el mínimo de participantes en el plazo (24-72h), la compra se confirma."
  },
  {
    step: 4,
    icon: PackageCheck,
    title: "Recibe o reembolso",
    description: "Si se confirma, recibes tu producto. Si no, recuperas tu dinero automáticamente."
  }
];

const features = [
  {
    icon: Users,
    title: "Compra en Grupo",
    description: "Únete con otros compradores y accede a precios exclusivos"
  },
  {
    icon: Clock,
    title: "Tiempo Limitado",
    description: "Ofertas de 24, 48 o 72 horas. Si no se alcanza el mínimo, te devolvemos el dinero"
  },
  {
    icon: Shield,
    title: "Pago Seguro",
    description: "Tu dinero está protegido hasta que se confirme la compra grupal"
  }
];

export default function Home({ user, logout }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCondition, setSelectedCondition] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    fetchPackages();
  }, [selectedCondition, selectedCategory]);

  const fetchPackages = async () => {
    try {
      let url = `${API}/packages/active`;
      const params = new URLSearchParams();
      
      if (selectedCondition !== "all") {
        params.append("condition", selectedCondition);
      }
      if (selectedCategory !== "all") {
        params.append("category", selectedCategory);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url);
      setPackages(response.data);
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  };

  // Count packages by condition for badges
  const getConditionCount = (condition) => {
    if (condition === "all") return packages.length;
    return packages.filter(pkg => pkg.product_condition === condition).length;
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <Header user={user} logout={logout} />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50 via-white to-white" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="secondary" className="mb-6 px-4 py-2 text-sm font-medium bg-orange-50 text-orange-600 border-orange-200">
              Ahorra hasta un 30% en productos Apple
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Compras Grupales
              <span className="block text-orange-500">Apple</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Únete a otros compradores, alcanza el mínimo de participantes y consigue los mejores precios en productos Apple nuevos, usados y reacondicionados.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="h-14 px-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-lg"
                onClick={() => document.getElementById('deals-section').scrollIntoView({ behavior: 'smooth' })}
                data-testid="view-deals-btn"
              >
                Ver Ofertas Activas
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              {!user && (
                <Link to="/register">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="h-14 px-8 rounded-full font-medium text-lg border-2"
                    data-testid="register-btn"
                  >
                    Regístrate Gratis
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Hero Image */}
          <div className="mt-16 relative">
            <div className="aspect-[16/9] max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1695822958645-b2b058159215?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxpUGhvbmUlMjAxNSUyMHBybyUyMHRpdGFuaXVtJTIwbmF0dXJhbHxlbnwwfHx8fDE3NzA4NTIzOTd8MA&ixlib=rb-4.1.0&q=85"
                alt="iPhone Pro"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-orange-500 text-white border-orange-500">
              Así de fácil
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              ¿Cómo funciona BulkBytes?
            </h2>
            <p className="text-lg text-primary-foreground/80 max-w-2xl mx-auto">
              Compra en grupo, ahorra dinero. Si no se alcanza el mínimo, recuperas tu dinero. Sin riesgo.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((item, index) => (
              <div 
                key={index}
                className="relative p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/15 transition-colors"
                data-testid={`how-it-works-${index}`}
              >
                {/* Step number */}
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-sm">
                  {item.step}
                </div>
                
                {/* Arrow connector (hidden on mobile and last item) */}
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-orange-500">
                    <ArrowRight className="h-6 w-6" />
                  </div>
                )}
                
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-4 mt-2">
                  <item.icon className="h-6 w-6 text-orange-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-sm text-primary-foreground/70">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          {/* Key benefit callout */}
          <div className="mt-12 p-6 bg-orange-500/20 rounded-2xl border border-orange-500/30 flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <div className="w-14 h-14 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="h-7 w-7 text-white" />
            </div>
            <div>
              <p className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Sin riesgo: Si no se completa el grupo, tu dinero vuelve automáticamente
              </p>
              <p className="text-sm text-primary-foreground/70 mt-1">
                El precio solo se bloquea si se alcanza el mínimo de participantes en el tiempo establecido
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              ¿Por qué BulkBytes?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Ventajas de comprar en grupo
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-8 bg-white rounded-2xl border border-border/40 hover:bg-gray-50/50 transition-colors"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center mb-6">
                  <feature.icon className="h-6 w-6 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-muted-foreground">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deals Section */}
      <section id="deals-section" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Ofertas Activas
              </h2>
              <p className="text-muted-foreground">
                Únete antes de que acabe el tiempo
              </p>
            </div>
          </div>

          {/* Condition Tabs (Primary Filter) */}
          <Tabs value={selectedCondition} onValueChange={setSelectedCondition} className="mb-8">
            <TabsList className="h-auto p-1 bg-gray-100/80 rounded-xl" data-testid="condition-tabs">
              {conditions.map((cond) => (
                <TabsTrigger 
                  key={cond.id} 
                  value={cond.id}
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-6 py-3"
                  data-testid={`condition-${cond.id}`}
                >
                  <div className="flex items-center gap-2">
                    {cond.icon && <cond.icon className="h-4 w-4" />}
                    <span className="font-medium">{cond.label}</span>
                  </div>
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Category Filter (Secondary) */}
            <div className="flex flex-wrap gap-2 mt-6 mb-8" data-testid="category-filter">
              {categories.map((cat) => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? "default" : "outline"}
                  size="sm"
                  className={`rounded-full ${selectedCategory === cat.id ? 'bg-primary text-primary-foreground' : ''}`}
                  onClick={() => setSelectedCategory(cat.id)}
                  data-testid={`category-${cat.id}`}
                >
                  {cat.icon && <cat.icon className="h-4 w-4 mr-2" />}
                  {cat.label}
                </Button>
              ))}
            </div>

            {/* Packages Grid */}
            {conditions.map((cond) => (
              <TabsContent key={cond.id} value={cond.id} className="mt-0">
                {loading ? (
                  <div className="flex justify-center py-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : packages.length === 0 ? (
                  <div className="text-center py-20">
                    <p className="text-muted-foreground text-lg">
                      No hay ofertas activas con estos filtros
                    </p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => {
                        setSelectedCondition("all");
                        setSelectedCategory("all");
                      }}
                    >
                      Ver todas las ofertas
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="packages-grid">
                    {packages.map((pkg, index) => (
                      <DealCard key={pkg.package_id} package={pkg} index={index} />
                    ))}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            ¿Listo para ahorrar?
          </h2>
          <p className="text-lg text-primary-foreground/80 max-w-2xl mx-auto mb-8">
            Regístrate gratis y empieza a unirte a las ofertas grupales. Tu dinero está protegido hasta que se confirme la compra.
          </p>
          {!user && (
            <Link to="/register">
              <Button 
                size="lg" 
                className="h-14 px-8 rounded-full bg-orange-500 text-white hover:bg-orange-600 font-medium text-lg"
                data-testid="cta-register-btn"
              >
                Crear Cuenta Gratis
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t bg-gray-50" data-testid="main-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  BulkBytes
                </span>
              </div>
              <p className="text-sm text-muted-foreground max-w-sm">
                Compras grupales de productos Apple. Ahorra comprando en grupo con total seguridad.
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Información
              </h4>
              <ul className="space-y-2">
                <li>
                  <a href="#how-it-works" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Cómo funciona
                  </a>
                </li>
                <li>
                  <a href="#deals-section" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Ofertas activas
                  </a>
                </li>
                <li>
                  <a href="#faq-section" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Preguntas frecuentes
                  </a>
                </li>
                <li>
                  <Link to="/partners" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-partners-link">
                    Ser Partner
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Legal
              </h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-privacy-link">
                    Política de Privacidad
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-terms-link">
                    Términos y Condiciones
                  </Link>
                </li>
                <li>
                  <Link to="/warranties" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-warranties-link">
                    Garantías y Devoluciones
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="pt-8 border-t border-gray-200">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-muted-foreground">
                © 2025 BulkBytes. Todos los derechos reservados.
              </p>
              <div className="flex items-center gap-4">
                <span className="text-xs text-muted-foreground">
                  Pagos seguros
                </span>
                <span className="text-muted-foreground">·</span>
                <Link to="/admin" className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-admin-link">
                  Admin
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
